import { endOfMonth, endOfQuarter, startOfMonth, startOfQuarter } from "date-fns";
import type React from "react";
import { twJoin } from "tailwind-merge";

import { Button, buttonVariants } from "@/design-system/Button";
import { twResolve } from "@/helpers/tw-resolve";
import { useBool } from "@/hooks/useBool";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { useOnScroll } from "@/hooks/useOnScroll";
import { useTranslation } from "@/translations";
import type { PickerRef } from "@/ui-adapters/AntDatePicker";

import { AntDatePicker } from "../DatePicker/picker";
import { connectedPickerPlacements } from "../DatePicker/placements";
import { useConnectedPickerPlacement } from "../DatePicker/useConnectedPickerPlacement";
import { formatDate } from "../FormattedDate/FormattedDate";
import { CalendarIcon } from "../Icons/CalendarIcon";

/** Props for selecting a month or quarter, including optional popup placement and responsive container styling. */
export interface PeriodPickerProps extends React.RefAttributes<PickerRef> {
  id?: string;
  name?: string;
  /** Fills the width of its container instead of using the default fixed minimum width. */
  fullWidth?: boolean;
  placement?: "bottomLeft" | "bottomRight" | "topLeft" | "topRight";
  value: Date;
  min?: Date;
  max?: Date;
  isError?: boolean;
  "data-testid"?: string;
  showNow?: boolean;
  // Returns the start and end date of the selected period
  onChange: (startDate: Date, endDate: Date) => void;
  renderSelected?: (date: Date) => string;
  renderFooterComponent?: (onClose: () => void) => React.ReactNode;
  ref?: React.Ref<PickerRef>;
  type: "quarter" | "month";
}

export function PeriodPicker({
  value,
  min,
  max,
  isError,
  ref,
  type,
  showNow,
  onChange,
  renderSelected,
  renderFooterComponent,
  fullWidth,
  placement,
  ...otherProps
}: PeriodPickerProps): React.ReactNode {
  const clockDate = useCurrentDate();
  const [isOpened, openHandler] = useBool(false);
  const connectedPickerPlacement = useConnectedPickerPlacement(isOpened);

  const { t, i18n } = useTranslation();

  useOnScroll(() => {
    openHandler.setFalse();
  });

  const handleChange = (date: Date | null) => {
    if (!date) {
      return;
    }

    if (type === "month") {
      onChange(startOfMonth(date), endOfMonth(date));
    } else {
      onChange(startOfQuarter(date), endOfQuarter(date));
    }

    openHandler.setFalse();
  };

  const isDateDisabled = (date: Date) => {
    if (min && date < min) {
      return true;
    }

    if (max && date > max) {
      return true;
    }

    return false;
  };

  const todayDate = clockDate;
  const shouldShowNowButton = showNow && !isDateDisabled(todayDate);

  return (
    <AntDatePicker
      {...otherProps}
      className={twJoin(
        fullWidth ? "w-full min-w-0" : "min-w-select-responsive-filter",
        connectedPickerPlacement.inputClassName,
      )}
      ref={ref}
      placement={placement}
      // oxlint-disable-next-line tailwindcss/no-unknown-classes -- These are stable CSS hooks, not Tailwind utilities.
      classNames={{ popup: { root: twJoin("period-picker-popup", connectedPickerPlacement.popupClassName) } }}
      open={isOpened}
      onOpenChange={openHandler.set}
      builtinPlacements={connectedPickerPlacements}
      picker={type}
      onChange={handleChange}
      disabledDate={isDateDisabled}
      format={
        renderSelected ? renderSelected : (date) => formatDate(i18n, type === "month" ? "monthYear" : "quarter", date)
      }
      value={value}
      status={isError ? "error" : undefined}
      allowClear={false}
      needConfirm={false}
      size="large"
      suffixIcon={<CalendarIcon size={16} tone="secondary" />}
      cellRender={(currStartOfPeriodDate, { type: activeType }) => {
        if (!(currStartOfPeriodDate instanceof Date)) {
          return null;
        }

        const isSelected = currStartOfPeriodDate.getTime() === value.getTime();
        const isDisabled = isDateDisabled(currStartOfPeriodDate);

        return (
          <button
            type="button"
            className={twResolve(
              buttonVariants({ variant: "subtle", size: "md", inset: "compact" }),
              "size-auto rounded border border-transparent p-space-sm text-center text-caption transition-colors duration-100 hover:bg-background-brand-light active:scale-100 disabled:cursor-not-allowed disabled:border-transparent disabled:text-text-disabled",
              isSelected ? "border-border-brand bg-background-brand-light" : undefined,
              activeType === "quarter" && "size-control-md",
              activeType === "month" && "size-control-xl",
              activeType === "year" && "h-control-md w-period-year-cell",
            )}
            disabled={isDisabled}
            data-testid="period-picker-cell-button"
            key={currStartOfPeriodDate.getMonth()}
          >
            {activeType === "month" && formatDate(i18n, "monthShort", currStartOfPeriodDate)}
            {activeType === "quarter" && formatDate(i18n, "quarterShort", currStartOfPeriodDate)}
            {activeType === "year" && currStartOfPeriodDate.getFullYear()}
          </button>
        );
      }}
      renderExtraFooter={
        shouldShowNowButton || renderFooterComponent
          ? () => (
              <>
                {shouldShowNowButton && (
                  <footer className="flex justify-center p-space-sm">
                    <Button
                      variant="ghostPrimary"
                      size="sm"
                      data-testid="period-picker-now-button"
                      onClick={() => handleChange(new Date())}
                    >
                      {t("component.date-picker.btn.now")}
                    </Button>
                  </footer>
                )}
                {renderFooterComponent ? renderFooterComponent(openHandler.setFalse) : null}
              </>
            )
          : undefined
      }
    />
  );
}
